import { formatIstDate } from '@tms/core';

/**
 * `LED-02`'s words, from the screen book (`STD-002 §7`, `FEAT-011 §9`).
 *
 * Copy written into JSX is copy nobody reviews, and every string here is part of a promise about
 * somebody's money.
 */
export const newEntryCopy = {
  eyebrow: 'New entry',
  title: 'Record a transaction',

  /**
   * The `✕`, not a `←` (`FEAT-011 §2.1`). Back means return to where you came from; a cross on a
   * half-filled form means throw this away, and somebody who has typed an amount deserves to know which
   * one they are pressing.
   */
  dismiss: 'Discard this entry',

  amountLabel: 'Amount',
  dateLabel: 'Date',
  purposeLabel: 'Purpose — tap + to add your own',
  noteLabel: 'Note (optional)',
  submit: 'Add to ledger',

  /**
   * Shown only after an attempt. A form that turns red while somebody is still typing has told them off
   * for not having finished.
   */
  amountRequired: 'An amount has to be more than zero.',
  categoryRequired: 'Pick a purpose.',

  categoriesFailed: 'We could not load your purposes. Check your connection and try again.',

  /**
   * The save failed and the form is still here with everything in it — which is the point, and is why
   * this says "try again" rather than apologising.
   */
  saveFailed: 'That did not save. Your entry is still here — try again.',
} as const;

/**
 * `LED-04`'s words (`STD-002 §7`, `FEAT-011 §9`).
 */
export const entryCopy = {
  title: 'Entry',
  detailsHeading: 'Details',

  type: 'Type',
  debit: 'Debit',
  credit: 'Credit',
  purpose: 'Purpose',
  added: 'Added',
  lastEdited: 'Last edited',
  synced: 'Synced',

  /**
   * An em dash, because the entry has never been edited. It comes from the server sending null rather
   * than from comparing two timestamps, which is a comparison that goes wrong the day a create and an
   * edit land in the same millisecond (`FEAT-011 §5.3`).
   */
  never: '—',

  edit: 'Edit',
  delete: 'Delete',
  trustLine: 'Deleted entries wait 3 days in Recently deleted before they are gone for good',

  deletedToast: 'Entry deleted',
  undo: 'Undo',

  editTitle: 'Change this entry',
  amountLabel: 'Amount',
  purposeLabel: 'Purpose',
  noteLabel: 'Note (optional)',
  cancel: 'Cancel',
  save: 'Save',

  /** Somebody else got there first, and the answer carries what they did. Not a failure to retry blindly. */
  staleEdit: 'Somebody edited this on another device. Open it again to see what changed.',
  saveFailed: 'That did not save. Your changes are still here — try again.',

  /**
   * The same words for "no such entry" and "not yours", because the API answers both with one `404` and a
   * screen that distinguished them would undo the reason it does (`ARCH-003 §2` rule 2).
   */
  notFound: 'That is not here. It may have been deleted.',
} as const;

/** `LED-05`'s words (`STD-002 §7`). */
export const ledgerCopy = {
  title: 'Full ledger',
  addEntry: 'Add an entry',
  eyebrow: 'Every entry, running balance included',

  filterLabel: 'Filter entries',
  all: 'All',
  credit: 'Credit',
  debit: 'Debit',

  /**
   * `LED-05`'s "August ▾" (`FEAT-011 §2.1`). The list is the current payday month and the eleven
   * before it, computed on the client from the same rule the server resolves `?month=` with.
   */
  monthLabel: 'Month',
  allTime: 'All time',

  more: 'Load more',
  loadingMore: 'Loading…',

  emptyTitle: 'Nothing recorded yet',
  emptyBody: 'Tap + to add your first entry. It takes about five seconds.',

  failed: 'We could not load your ledger. Check your connection and try again.',
} as const;

/** `LED-07`'s words, from the screen book (`STD-002 §7`, `FEAT-011 §9`). */
export const trashCopy = {
  title: 'Recently deleted',
  eyebrow: 'Kept for 3 days, then removed for good',

  /**
   * A countdown per entry, not one blanket rule — two entries deleted on different days have different
   * amounts of time left, and one line about the rule is useless about either of them.
   *
   * "Last day" rather than "0 days left": the entry is still restorable, and a zero reads as gone.
   */
  countdown: (days: number): string =>
    days <= 0 ? 'Last day' : days === 1 ? '1 day left' : `${String(days)} days left`,

  deletedOn: (deletedAtUtc: string): string => `deleted ${formatIstDate(new Date(deletedAtUtc))}`,

  restoreAll: 'Restore all',
  emptyNow: 'Empty now',

  explanation:
    'Restoring puts the entry back on its original date, and the running balance recalculates. Nothing is silently renumbered.',

  emptyTitle: 'Nothing here',
  emptyBody: 'Deleted entries will wait three days before they disappear.',

  failed: 'We could not load Recently deleted. Check your connection and try again.',
} as const;
